import {
  Box,
  CircularProgress,
  Divider,
  Popover,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";

import { usePerspective } from "../../contexts/app/provider";
import { usePerspectiveAuthRetry } from "../../services/perspective_api/use_auth_retry";
import { GetTransformedRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { parseParquetData, FullParsedParquetData } from "../../utils/parquet";
import { RunSettings } from "../../services/appsync/types";
import {
  closeForecastedSalesCellPopover,
  useForecastedSalesCellPopoverTarget,
} from "./store";

interface ForecastedSalesRow {
  /** "sales" (a channel — a retailer, on Cora) or "transfer" (a destination). */
  source_kind: string;
  source_label: string;
  /** Exact demand this source contributed to the cell's period. */
  demand_units: number;
  demand_share: number | null;
  cell_demand_total: number | null;
  /** Uncapped source count (rows are capped at 12 server-side). */
  total_source_count: number | null;
}

interface FetchEntry {
  loading: boolean;
  error: string | null;
  rows: ForecastedSalesRow[] | null;
}

// Module-level cache so reopening a cell never refetches within a session.
// Keyed by run + cell identity; a new run settings object is a new key.
const detailCache = new Map<string, FetchEntry>();

const IDLE_ENTRY: FetchEntry = { loading: false, error: null, rows: null };
const LOADING_ENTRY: FetchEntry = { loading: true, error: null, rows: null };

const cacheKeyFor = (
  runSettings: RunSettings | null,
  t: {
    sku: unknown;
    location: unknown;
    periodStartMs: unknown;
    dateHierarchy: unknown;
  },
): string =>
  JSON.stringify([
    runSettings,
    t.sku,
    t.location,
    t.periodStartMs,
    t.dateHierarchy,
  ]);

const toNumberOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v); // arrow Int64 arrives as bigint
  return Number.isNaN(n) ? null : n;
};

// The transform also returns revenue / unit_price / cell_revenue_total /
// revenue_is_partial. They are deliberately NOT read: dollar figures on Cora
// are inflated by an each multiplier until COR-77 lands, and this popover must
// not become a second place that shows them.
function arrowToRows(parsed: FullParsedParquetData): ForecastedSalesRow[] {
  return parsed.arrowTable.toArray().map((r: any) => ({
    source_kind: String(r.source_kind ?? ""),
    source_label: String(r.source_label ?? ""),
    demand_units: toNumberOrNull(r.demand_units) ?? 0,
    demand_share: toNumberOrNull(r.demand_share),
    cell_demand_total: toNumberOrNull(r.cell_demand_total),
    total_source_count: toNumberOrNull(r.total_source_count),
  }));
}

const formatQty = (n: number | null): string =>
  n == null ? "—" : n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatShare = (s: number | null): string =>
  s == null ? "—" : `${(s * 100).toFixed(s < 0.1 ? 1 : 0)}%`;

const FIELD_LABEL_SX = {
  fontSize: "10.5px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  color: (t: any) => t.palette.atomic.muted,
};

/** Distinguishes a sales channel from a transfer destination at a glance. */
function SourceChip({ kind }: { kind: string }) {
  const isTransfer = kind === "transfer";
  return (
    <Box
      sx={{
        display: "inline-block",
        fontSize: "9.5px",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        borderRadius: "4px",
        padding: "1px 5px",
        color: (t) => t.palette.atomic.ink,
        backgroundColor: (t) =>
          isTransfer ? t.palette.atomic.orange1 : t.palette.action.selected,
      }}
    >
      {isTransfer ? "transfer" : "sales"}
    </Box>
  );
}

/**
 * Popover breaking one "forecasted sales" cell into the demand behind it —
 * sales by channel (retailers, on Cora), plus transfers by destination on hub
 * cells (where the displayed figure is sales + transfer departures).
 *
 * Every number here is exact: the simulation holds per-channel demand
 * directly, so nothing is allocated or estimated (unlike the unfulfilled-
 * demand popover). Units and share only — see arrowToRows for why revenue is
 * left out.
 *
 * Render-isolation contract: this component (and its module store) is the ONLY
 * thing that re-renders on open/close/data-arrival. It must never feed state
 * back into the matrix table — the clicked-cell outline is applied
 * imperatively to the anchor element for the same reason.
 */
export function ForecastedSalesCellPopover({
  runSettings,
}: {
  runSettings: RunSettings | null;
}) {
  const target = useForecastedSalesCellPopoverTarget();
  const { perspectiveApiService } = usePerspective();
  const theme = useTheme();
  const outlineColor = theme.palette.atomic.brandBlue;
  // The entry is stamped with the cell it was fetched for. Switching cells
  // (A open → click B) would otherwise paint one frame of A's rows under B's
  // anchor before the fetch effect flips to loading.
  const [entryState, setEntryState] = useState<{
    key: string | null;
    entry: FetchEntry;
  }>({ key: null, entry: IDLE_ENTRY });
  // Stale socket token (SUNGLAS-39): one recovery per cell, then resubscribe.
  const { retryNonce, recoverFrom, markSucceeded } = usePerspectiveAuthRetry(
    perspectiveApiService,
  );
  const cacheKey = target ? cacheKeyFor(runSettings, target) : null;
  const entry =
    cacheKey != null && entryState.key === cacheKey
      ? entryState.entry
      : LOADING_ENTRY;

  // Outline the clicked cell while the popover is open (imperative on purpose
  // — see render-isolation contract above).
  useEffect(() => {
    if (!target) return;
    const el = target.anchorEl;
    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    el.style.outline = `2px solid ${outlineColor}`;
    el.style.outlineOffset = "-2px";
    return () => {
      el.style.outline = prevOutline;
      el.style.outlineOffset = prevOffset;
    };
  }, [target, outlineColor]);

  useEffect(() => {
    if (!target) return;
    // A late async continuation (parseParquetData can do a network fetch) must
    // not write into a popover that's since re-anchored to another cell.
    let cancelled = false;
    const key = cacheKeyFor(runSettings, target);
    const setEntry = (e: FetchEntry) => setEntryState({ key, entry: e });
    // Only completed fetches count as cache hits. A stale {loading:true} entry
    // (popover closed mid-flight) or an error entry must refetch, or reopening
    // would spin forever.
    const cached = detailCache.get(key);
    if (cached?.rows) {
      setEntry(cached);
      return;
    }
    detailCache.set(key, LOADING_ENTRY);
    setEntry(LOADING_ENTRY);

    // Failure is latched here and resolved on `complete` — see the handlers.
    let pendingError: string | null = null;
    let sawData = false;

    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({
        query: GetTransformedRawParquetDocument,
        variables: {
          datasetName: "supply_walk_test",
          transformId: "forecasted_sales_details",
          folder: "outputs",
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: null,
          format: "parquet" as const,
          params: {
            sku: target.sku,
            // Omit location for SKU-level cells so the transform aggregates
            // across every location.
            ...(target.location != null ? { location: target.location } : {}),
            period_start: new Date(target.periodStartMs).toISOString(),
            date_hierarchy: target.dateHierarchy,
          },
        },
      })
      .subscribe({
        next: async (res: any) => {
          const payload = res?.data?.getTransformedRawParquet;
          const parquetData = payload?.parquetData;
          // Latch a failure rather than rendering it straight away. The
          // server's FIRST emit reports cached state, which can be a previous
          // attempt's failure that the fresh run then supersedes — rendering
          // it immediately flashes an error a moment before the data lands.
          // `complete` decides, once we know nothing better is coming.
          if (payload?.status === "FAILED" || payload?.errorMessage) {
            pendingError = String(
              payload?.errorMessage || "Transformation failed",
            );
            return;
          }
          // Still in flight (LOADING, or a progress message with no payload).
          if (!parquetData || typeof parquetData !== "string") return;
          // Set before awaiting: `complete` can fire while the parse is still
          // in flight, and must not mistake that for "no data arrived".
          sawData = true;
          pendingError = null;
          try {
            const parsed = await parseParquetData(
              parquetData,
              !parquetData.startsWith("http"),
            );
            const rows = arrowToRows(parsed as FullParsedParquetData);
            const done: FetchEntry = { loading: false, error: null, rows };
            detailCache.set(key, done);
            // Loaded: re-arm this cell so a later token expiry can recover.
            markSucceeded(key);
            if (!cancelled) setEntry(done);
          } catch (e: any) {
            const failed: FetchEntry = {
              loading: false,
              error: String(e?.message || e),
              rows: null,
            };
            detailCache.set(key, failed);
            if (!cancelled) setEntry(failed);
          }
        },
        error: (err: any) => {
          const message = String(err?.message || err);
          const fail = (reason: string) => {
            const failed: FetchEntry = {
              loading: false,
              error: reason,
              rows: null,
            };
            detailCache.set(key, failed);
            if (!cancelled) setEntry(failed);
          };
          // An auth rejection means the socket's token went stale. Recover
          // once per cell and resubscribe; anything else — or a rejection
          // after recovery — fails normally, so it can never loop.
          if (
            recoverFrom(key, message, {
              isCancelled: () => cancelled,
              onGiveUp: fail,
            })
          ) {
            detailCache.delete(key);
            return;
          }
          fail(message);
        },
        complete: () => {
          // The stream ended. If data arrived, the handler above already
          // rendered it. Otherwise surface the latched failure — or say the
          // request returned nothing, so a silent server-side failure can
          // never leave the popover spinning forever.
          if (sawData) return;
          const failed: FetchEntry = {
            loading: false,
            error: pendingError ?? "No data returned for this cell",
            rows: null,
          };
          detailCache.set(key, failed);
          if (!cancelled) setEntry(failed);
        },
      });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [
    target,
    runSettings,
    perspectiveApiService,
    retryNonce,
    recoverFrom,
    markSucceeded,
  ]);

  if (!target) return null;

  return (
    <Popover
      open
      anchorEl={target.anchorEl}
      onClose={closeForecastedSalesCellPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            padding: "12px 14px",
            minWidth: 260,
            maxWidth: 340,
          },
        },
      }}
    >
      <PopoverBody entry={entry} cellValue={target.cellValue ?? null} />
    </Popover>
  );
}

function PopoverBody({
  entry,
  cellValue,
}: {
  entry: FetchEntry;
  cellValue: number | null;
}) {
  if (entry.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "8px" }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (entry.error) {
    return (
      <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.red1 }}>
        Couldn't load sales sources: {entry.error}
      </Box>
    );
  }
  const rows = entry.rows ?? [];
  if (rows.length === 0) {
    return (
      <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.muted }}>
        No sales sources available for this cell.
      </Box>
    );
  }

  const cellDemand = rows[0]?.cell_demand_total ?? null;
  const totalCount = rows[0]?.total_source_count ?? rows.length;
  // Rows are capped server-side, so a hub cell's transfer destinations can all
  // fall below the cut while the total still includes them. Only describe the
  // mix from the rows when the list is complete; otherwise stay neutral.
  const isCapped = totalCount > rows.length;
  const hasTransfers = rows.some((row) => row.source_kind === "transfer");
  const subtitle = isCapped
    ? "largest sources; the total covers all of them"
    : hasTransfers
      ? "sales by channel plus transfers to spokes"
      : "by sales channel";
  // The grid shows hub outflows as negatives; this popover reports
  // magnitudes. Say so, so "-7,049" against "7,049" doesn't read as a bug.
  const shownAsOutflow = cellValue != null && cellValue < 0;

  return (
    <Box>
      <Box sx={{ mb: "8px" }}>
        <Box sx={FIELD_LABEL_SX}>Forecasted sales</Box>
        <Box
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            color: (t) => t.palette.atomic.ink,
          }}
        >
          {formatQty(cellDemand)}
        </Box>
        <Box sx={{ fontSize: "11px", color: (t) => t.palette.atomic.muted }}>
          {subtitle}
        </Box>
        {shownAsOutflow && (
          <Box sx={{ fontSize: "11px", color: (t) => t.palette.atomic.muted }}>
            shown as {formatQty(cellValue)} in the grid (an outflow)
          </Box>
        )}
      </Box>

      {totalCount > rows.length && (
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: (t) => t.palette.atomic.muted,
            mb: "6px",
          }}
        >
          Showing top {rows.length} of {totalCount} sources
        </Box>
      )}

      <Box sx={{ maxHeight: 320, overflowY: "auto", pr: "2px" }}>
        {rows.map((row, i) => (
          <Box key={`${row.source_kind}-${row.source_label}-${i}`}>
            {i > 0 && <Divider sx={{ my: "8px" }} />}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                mb: "4px",
              }}
            >
              <SourceChip kind={row.source_kind} />
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: (t) => t.palette.atomic.ink,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.source_label}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: "16px",
                py: "2px",
              }}
            >
              <Box sx={FIELD_LABEL_SX}>Units</Box>
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: (t) => t.palette.atomic.ink,
                }}
              >
                {formatQty(row.demand_units)}
                <Box
                  component="span"
                  sx={{
                    ml: "6px",
                    fontSize: "12px",
                    fontWeight: 400,
                    color: (t) => t.palette.atomic.muted,
                  }}
                >
                  {formatShare(row.demand_share)}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
